import {AppActions, useAppContext} from "../../context/AppContext";
import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Radio, Switch, Button, Tooltip} from "antd";
import Plot2Cell from "./Plot2Cell";
import {readPlot2, writePlot2} from "./plot2Store";

// Plot-2 tab: an all-XS station-elevation browser (modelled on the Map tab's
// river/XS browse UX). It reuses a FORK of the plot renderer
// (StationElevationPlot2) so the existing Plot tab / StationElevationPlot stay
// untouched as fallback. Data comes from getXsPlots() (independent of map_base).
// Selections persist across tab switches via plot2Store (this component unmounts
// when you leave the tab).
const GRIDS = {
    '1x1': {rows: 1, cols: 1},
    '1x2': {rows: 1, cols: 2},
    '2x1': {rows: 2, cols: 1},
    '2x2': {rows: 2, cols: 2},
}

const GAP = 8

function Plot2Toolbar({grid, onGridChange, sync, onSyncChange}) {
    return (
        <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
            <Radio.Group
                size={'small'}
                value={grid}
                onChange={(e) => onGridChange(e.target.value)}
                optionType="button"
                buttonStyle="solid"
            >
                {Object.keys(GRIDS).map(g => (
                    <Radio key={g} value={g} style={{width: 52}}>{g}</Radio>
                ))}
            </Radio.Group>
            <span style={{fontSize: 12, whiteSpace: 'nowrap'}}>
                <Switch size={'small'} checked={sync} onChange={onSyncChange}/> <b>Sync layers</b>
                <Tooltip title={'When on, all panels share one layer set — change layers in any panel and the others follow. When off, each panel is independent.'}>
                    <span style={{marginLeft: 4, color: '#999', cursor: 'help'}}>ⓘ</span>
                </Tooltip>
            </span>
        </div>
    )
}

export default function Plot2Tab() {
    const {dispatchProject, selectedHistoryId} = useAppContext()
    const tabKey = `${selectedHistoryId}:tab`
    const saved = readPlot2(tabKey)
    const [grid, setGrid] = useState(saved.grid || '1x1')
    const [sync, setSync] = useState(saved.sync || false)
    // Shared legend selection (used by every cell while "Sync layers" is on).
    const [sharedLayers, setSharedLayers] = useState(saved.sharedLayers ?? null)
    // When set, only this cell is shown, full size (fullscreen / focus mode).
    const [fullscreenIndex, setFullscreenIndex] = useState(saved.fullscreenIndex ?? null)
    const containerRef = useRef()
    const [containerWidth, setContainerWidth] = useState(0)

    // Persist tab-level selections so they survive tab switches (keyed by run id).
    useEffect(() => {
        writePlot2(tabKey, {grid, sync, sharedLayers, fullscreenIndex})
    }, [tabKey, grid, sync, sharedLayers, fullscreenIndex]);

    // Inject the grid selector + sync toggle into the shared page toolbar (same
    // mechanism the Plot/Map tabs use). Cleared on unmount.
    useEffect(() => {
        dispatchProject({
            type: AppActions.SET_MESSAGE_TOOLBAR_ITEM,
            data: {toolbar: (<Plot2Toolbar grid={grid} onGridChange={setGrid} sync={sync} onSyncChange={setSync}/>)}
        })
        return () => {
            dispatchProject({type: AppActions.SET_MESSAGE_TOOLBAR_ITEM, data: null})
        }
    }, [grid, sync]);

    // Measure container so each cell's plot can be sized to fit (and re-scale on a
    // debounced window resize — the fork picks up the new width/height props).
    useLayoutEffect(() => {
        let t = null
        const measure = () => setContainerWidth(containerRef.current?.offsetWidth || 0)
        const onResize = () => {
            if (t) clearTimeout(t)
            t = setTimeout(measure, 150)
        }
        measure()
        window.addEventListener('resize', onResize)
        return () => {
            if (t) clearTimeout(t)
            window.removeEventListener('resize', onResize)
        }
    }, []);

    const {rows, cols} = GRIDS[grid]
    // Subtract each cell's chrome (Plot2Cell padding 12 + border 2) plus a right
    // gutter so the plot's bottom-right resize handle isn't flush with / clipped at
    // the edge for right-column panels in multi-view grids.
    const CELL_CHROME = 14
    const HANDLE_GUTTER = 18
    const cellWidth = containerWidth ? Math.floor((containerWidth - GAP * (cols - 1)) / cols) - CELL_CHROME - HANDLE_GUTTER : undefined
    const cellHeight = rows === 1 ? 500 : 380
    const cells = Array.from({length: rows * cols}, (_, i) => i)

    const cellProps = (i, w, h) => ({
        cellIndex: i,
        persistKey: `${selectedHistoryId}:${grid}:${i}`,
        width: w,
        height: h,
        sync,
        sharedLayers,
        onLayersChange: setSharedLayers,
        canExpand: (rows * cols) > 1,
        expanded: fullscreenIndex === i,
        onToggleExpand: () => setFullscreenIndex(fullscreenIndex === i ? null : i),
    })

    // Fullscreen / focus mode: render only the chosen cell, full size.
    if (fullscreenIndex != null) {
        const fw = containerWidth ? containerWidth - CELL_CHROME - HANDLE_GUTTER : undefined
        return (
            <div ref={containerRef} style={{width: '100%'}}>
                <div style={{marginBottom: 6}}>
                    <Button size={'small'} onClick={() => setFullscreenIndex(null)}>← Grid</Button>
                </div>
                {containerWidth > 0 && (
                    <Plot2Cell key={`${selectedHistoryId}-${grid}-${fullscreenIndex}`} {...cellProps(fullscreenIndex, fw, 640)}/>
                )}
            </div>
        )
    }

    return (
        <div
            ref={containerRef}
            style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gap: GAP,
                width: '100%',
                overflow: 'hidden',
            }}
        >
            {containerWidth > 0 && cells.map(i => (
                <Plot2Cell key={`${selectedHistoryId}-${grid}-${i}`} {...cellProps(i, cellWidth, cellHeight)}/>
            ))}
        </div>
    )
}
